/**
 * ImageId Generation Utilities
 *
 * Cornerstone3D identifies images by imageId strings with a scheme prefix.
 * This module provides utilities to generate proper imageIds.
 */
import { imageIdSourceUrl } from '@/lib/localGateway';
// ============================================
// Direct URL ImageIds
// ============================================
/**
 * Create the imageId(s) for a single instance — one per frame for multi-frame
 * objects. Shared by the stack builder below and by annotation persistence
 * (which needs a stable per-image mapping, not the flattened stack).
 *
 * Pure: the same image always produces the same ids. When the study has a LAN
 * URL the id is built from it unconditionally, and whether the bytes actually
 * come from the LAN is settled per-request in lib/localGateway/registerFallback.
 * Every caller must pass `localStorageLocation` when the DTO has one, or its ids
 * won't match the ones the viewports use.
 */
export function createImageIdsForImage(image) {
    const chosenUrl = imageIdSource(image);
    // No address at all, so no id: an instance whose pixels are still uploading —
    // or an onsite object the gateway has not uploaded yet — has no cloud URL and,
    // on a clinic without local serving, no LAN URL either. An id built from
    // nothing is not a usable address, and the rest of the viewer already assumes
    // such a row contributes none (see lib/viewer/seriesInstances'
    // healPendingInstances, which swaps the row in once its bytes land, and
    // lib/dicom/imageAvailability, which keeps its series or tile out of the
    // reader's way until then). It used to reach lib/localGateway and then
    // `.startsWith` on a missing value, which threw inside the stack builder's
    // flatMap and took down the whole pane for one unuploaded instance.
    if (!chosenUrl)
        return [];
    const numFrames = image.numberOfFrames ?? 1;
    const baseUrl = chosenUrl.startsWith('/') ? `${window.location.origin}${chosenUrl}` : chosenUrl;
    // Handle multi-frame images.
    // Cornerstone's wadouri parseImageId does `pixelDataFrame = frame - 1` (see
    // @cornerstonejs/dicom-image-loader/.../wadouri/parseImageId.js), so URL
    // frame numbers are 1-based (DICOM convention). Emitting 0-based indices
    // produces pixelDataFrame=-1, which dicomParser.readEncapsulatedImageFrame
    // rejects with "frameIndex must be >= 0".
    if (numFrames > 1) {
        return Array.from({ length: numFrames }, (_, i) => `wadouri:${baseUrl}${baseUrl.includes('?') ? '&' : '?'}frame=${i + 1}`);
    }
    return [`wadouri:${baseUrl}`];
}
/**
 * The URL this image's id is built from, or null when it has no address yet.
 *
 * Everything with a cloud URL goes through `imageIdSourceUrl`, which is the sole
 * owner of the local-vs-cloud choice and of the local -> cloud mapping the
 * request-time fallback reads. A row with NO cloud URL cannot be handed to it
 * (there is no cloud arm to map to, and nothing to fall back to), so its LAN URL
 * — the only address it has — is taken directly; that is the same URL
 * `imageIdSourceUrl` would have picked.
 */
function imageIdSource(image) {
    const cloud = image.storageLocation;
    if (!cloud)
        return image.localStorageLocation ?? null;
    return imageIdSourceUrl({ ...image, storageLocation: cloud });
}
/**
 * Narrow an API instance row to the fields imageId construction reads.
 *
 * Worth a named function rather than an inline object literal at each call
 * site: every caller must pass the same fields — `localStorageLocation` above
 * all — or it builds ids that don't match the ones the viewports use, and the
 * symptom is silent (a second cache entry, a second fetch), never an error.
 */
export function toSeriesImageInfo(image) {
    return {
        sopInstanceUID: image.imageUid,
        instanceNumber: image.instanceNumber,
        storageLocation: image.storageLocation,
        localStorageLocation: image.localStorageLocation,
        numberOfFrames: image.numberOfFrames ?? undefined,
    };
}
/**
 * Create imageIds from series image info (as returned by our API)
 *
 * @param images - Array of image info from GetStudyResponse
 * @returns Array of imageIds in wadouri: scheme, sorted by instance number
 */
export function createImageIdsFromSeries(images) {
    return sortForStack(images).flatMap(createImageIdsForImage);
}
/** Instances in stack order: by instance number, ties kept in row order. */
function sortForStack(images) {
    return images.toSorted((a, b) => (a.instanceNumber ?? 0) - (b.instanceNumber ?? 0));
}
/**
 * The instance the stack `createImageIdsFromSeries` builds from `images` shows
 * at `stackIndex` — one of the objects passed in, by reference — and where that
 * index falls within its frames. Null when the index is outside the stack.
 *
 * The inverse of {@link locateInstanceInStack}, walking the same sort and
 * expansion so the two can't disagree with the stack a viewport holds.
 */
export function instanceAtStackIndex(images, stackIndex) {
    if (stackIndex < 0)
        return null;
    let offset = 0;
    for (const image of sortForStack(images)) {
        const count = createImageIdsForImage(image).length;
        if (stackIndex < offset + count)
            return { image, firstIndex: offset, frameCount: count };
        offset += count;
    }
    return null;
}
/**
 * Where one instance sits in the stack `createImageIdsFromSeries` builds from
 * the same rows: the stack index of its first frame, and how many frames it
 * contributes (1 for a single-frame instance, one per frame otherwise).
 *
 * Walks the identical sort and per-instance expansion rather than restating
 * them, so the answer cannot drift from the stack a viewport actually holds:
 * a row with no address yet contributes no ids here exactly as it does there.
 * Null when the instance is not among `images`, or has no ids of its own.
 */
export function locateInstanceInStack(images, sopInstanceUID) {
    let offset = 0;
    for (const image of sortForStack(images)) {
        const count = createImageIdsForImage(image).length;
        if (image.sopInstanceUID === sopInstanceUID) {
            return count > 0 ? { firstIndex: offset, frameCount: count } : null;
        }
        offset += count;
    }
    return null;
}
// ============================================
// WADO-RS ImageIds (DICOMweb) - for future use
// ============================================
// ============================================
// File ImageIds (for drag-drop)
// ============================================
